/**
 * 🌍 Travel Detection Banner
 * Shows when the user's timezone changes (e.g., when traveling)
 * Allows user to acknowledge or update their country
 */
import React from 'react';
import { useLocale } from '../context/LocaleContext';
import { Plane, X, MapPin, Check } from 'lucide-react';
import { Button } from './ui/button';

const TravelDetectionBanner = () => {
  const { 
    travelDetection, 
    dismissTravelDetection, 
    acceptTimezoneChange,
    t 
  } = useLocale();

  if (!travelDetection?.detected) {
    return null;
  }

  return (
    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
      <div className="bg-gradient-to-r from-blue-500 to-teal-500 text-white rounded-2xl shadow-lg p-4 max-w-md mx-4">
        <div className="flex items-start gap-3">
          <div className="bg-white/20 rounded-full p-2 mt-0.5">
            <Plane className="h-5 w-5" />
          </div>
          
          <div className="flex-1">
            <h3 className="font-semibold text-lg mb-1">
              {t('travel.detected') || '🌍 New Location Detected!'}
            </h3>
            
            <p className="text-sm text-white/90 mb-3">
              {t('travel.movedFrom') || 'Looks like you moved from'}{' '}
              <span className="font-medium">{travelDetection.oldRegion}</span>
              {' '}{t('travel.to') || 'to'}{' '}
              <span className="font-medium">{travelDetection.flag} {travelDetection.newRegion}</span>
            </p>
            
            <div className="flex gap-2">
              {travelDetection.suggestedCountry && (
                <Button
                  size="sm"
                  onClick={() => acceptTimezoneChange(true)}
                  className="bg-white text-blue-600 hover:bg-white/90 font-medium"
                >
                  <MapPin className="h-4 w-4 mr-1" />
                  {t('travel.updateLocation') || 'Update Location'}
                </Button>
              )}
              
              <Button
                size="sm"
                variant="ghost"
                onClick={() => acceptTimezoneChange(false)}
                className="text-white hover:bg-white/20"
              >
                <Check className="h-4 w-4 mr-1" />
                {t('travel.gotIt') || 'Got it'}
              </Button>
            </div>
          </div>
          
          <button 
            onClick={dismissTravelDetection}
            className="text-white/70 hover:text-white transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TravelDetectionBanner;
